import { useEffect, useState } from "react";
import { Wrench, Loader2, CheckCircle2, AlertTriangle, History, Trash2, ListChecks } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { modeLabel } from "../../lib/modes";

const API = "/api";
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "");
const SOURCE_LABEL = { startup: "Automatic (server start)", manual: "Repair drills button", delete: "Question deleted", bulk_delete: "Bulk delete" };

function RepairEntry({ entry }) {
  return (
    <li className="bg-card rounded-lg border border-border/60 px-3 py-2 text-xs space-y-1" data-testid={`drill-repair-entry-${entry.log_id}`}>
      <div className="flex items-center gap-2 flex-wrap text-muted-foreground">
        <span className="font-medium text-foreground">{fmtWhen(entry.at)}</span>
        <Badge variant="outline" className="text-[10px]">{SOURCE_LABEL[entry.source] || entry.source}</Badge>
        {entry.actor && <span>by {entry.actor}</span>}
        {entry.progress_fixed > 0 && <span>· {entry.progress_fixed} student progress record{entry.progress_fixed === 1 ? "" : "s"} re-counted</span>}
      </div>
      {(entry.drills_updated || []).map((d) => (
        <p key={d.drill_id} className="flex items-center gap-1.5"><ListChecks className="w-3 h-3 text-amber-500 shrink-0" /> <span className="font-medium">{d.title}</span> <span className="text-muted-foreground">({modeLabel(d.mode)})</span> — {d.removed.length} question{d.removed.length === 1 ? "" : "s"} removed, {d.remaining} remain</p>
      ))}
      {(entry.drills_deleted || []).map((d) => (
        <p key={d.drill_id} className="flex items-center gap-1.5 text-red-600 dark:text-red-400"><Trash2 className="w-3 h-3 shrink-0" /> <span className="font-medium">{d.title}</span> <span className="text-muted-foreground">({modeLabel(d.mode)})</span> — deleted (no questions left){d.classes_unassigned ? `, unassigned from ${d.classes_unassigned} class${d.classes_unassigned === 1 ? "" : "es"}` : ""}</p>
      ))}
    </li>
  );
}

export function DrillRepairTool({ isMasterAdmin, onRepaired }) {
  const [health, setHealth] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/drills/health`, { credentials: "include" });
      if (res.ok) setHealth(await res.json());
    } catch { /* keep loading state */ }
  };
  useEffect(() => { load(); }, []);

  const repair = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/admin/drills/repair`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Repair failed"); return; }
      const n = (data.drills_updated || []).length, del = (data.drills_deleted || []).length;
      if (!n && !del) toast.success("All drills are healthy - nothing to repair");
      else toast.success(`Repaired ${n} drill${n === 1 ? "" : "s"}${del ? `, deleted ${del} empty drill${del === 1 ? "" : "s"}` : ""}${data.progress_fixed ? `, re-counted ${data.progress_fixed} student progress record${data.progress_fixed === 1 ? "" : "s"}` : ""}`);
      await load();
      onRepaired?.();
    } catch {
      toast.error("Repair failed");
    } finally {
      setRunning(false);
    }
  };

  const missing = health?.missing || [];
  const repairs = health?.repairs || [];

  return (
    <div className="border border-border rounded-xl p-4" data-testid="drill-repair-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-amber-500" />
            Drill Health
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            Finds drills that still point at deleted questions (students get stuck on those). Repair removes the missing questions, re-counts every student's progress and deletes drills left with no questions. This also runs automatically whenever a question is deleted and at every server start.
          </p>
        </div>
        {isMasterAdmin && (
          <Button onClick={repair} disabled={running || !health} variant="outline" className="gap-2 border-amber-400 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/40" data-testid="repair-drills-btn">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" />Repairing…</> : <><Wrench className="w-4 h-4" />Repair drills</>}
          </Button>
        )}
      </div>

      {!health ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="drill-health-loading"><Loader2 className="w-4 h-4 animate-spin" /> Checking drills…</p>
      ) : missing.length === 0 ? (
        <div className="border rounded-lg p-3 text-sm flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400" data-testid="drill-health-ok">
          <CheckCircle2 className="w-4 h-4" /> Every drill points at existing questions.
        </div>
      ) : (
        <div className="border rounded-lg p-3 bg-red-50/60 dark:bg-red-900/10 border-red-200 dark:border-red-800" data-testid="drill-health-missing">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 text-red-700 dark:text-red-400">
            <AlertTriangle className="w-3.5 h-3.5" /> {missing.length} drill{missing.length === 1 ? "" : "s"} with deleted questions
          </p>
          <ul className="space-y-1.5">
            {missing.map((d) => (
              <li key={d.drill_id} className="bg-card rounded-lg border border-border/60 px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid={`drill-missing-${d.drill_id}`}>
                <span className="font-medium truncate">{d.title}</span>
                <Badge variant="outline" className="text-[10px]">{modeLabel(d.mode)}</Badge>
                <span className="text-xs text-muted-foreground">{d.missing.length} missing · {d.remaining} remain{d.remaining === 0 ? " → drill will be deleted" : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {repairs.length > 0 && (
        <div className="mt-4" data-testid="drill-repair-history">
          <p className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 text-muted-foreground"><History className="w-3.5 h-3.5" /> Repair history</p>
          <ul className="space-y-1.5">{repairs.map((e) => <RepairEntry key={e.log_id} entry={e} />)}</ul>
        </div>
      )}
    </div>
  );
}
